import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, X, Dog, Cat, Megaphone, Wrench } from 'lucide-react';
import Spinner from '../UIFeedback/Spinner/Spinner';
import { formData } from './formData';
import FieldComponent from './FieldComponent';
import useCreateNotification from '../../hooks/notifications/useCreateNotification';
import { selectEffectiveCoordinates, selectEffectiveLocationName } from '../../slices/locationSlice';
import { getValueByName } from '../../utils/getValueByName';
import useCloudFlareImages from '../../hooks/useCloudFlareImages';
import useProfanityFilter from '../../hooks/useProfanityFilter';

/**
 * Type selection cards for when no initialType is provided
 */
const TYPE_OPTIONS = [
  { value: 'psa', label: 'psa', icon: Megaphone, color: 'amber' },
  { value: 'lost-animal', label: 'lostAnimal', icon: Dog, color: 'red' },
  { value: 'found-animal', label: 'foundAnimal', icon: Cat, color: 'green' },
  { value: 'pet-service', label: 'petService', icon: Wrench, color: 'purple' },
];

const getTypeColor = (type) => {
  const colors = {
    'psa': { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-500', ring: 'ring-amber-500' },
    'lost-animal': { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-500', ring: 'ring-red-500' },
    'found-animal': { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-500', ring: 'ring-green-500' },
    'pet-service': { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-500', ring: 'ring-purple-500' },
  };
  return colors[type] || colors['psa'];
};

/**
 * CreatePSAForm - Form for creating PSAs, Lost Animals, and Found Animals
 * 
 * @param {string} initialType - Pre-selected type (skips type selection)
 * @param {function} onSuccess - Callback when form is successfully submitted
 * @param {boolean} isModal - Whether the form is rendered inside a modal
 */
const CreatePSAForm = ({ initialType = null, onSuccess = null, isModal = false }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Memoize formStructure to prevent infinite re-renders
  // Only recreate when translation function changes (language switch)
  const formStructure = useMemo(() => formData(t), [t]);
  
  const [selectedType, setSelectedType] = useState(initialType || '');
  const [formFields, setFormFields] = useState(() => {
    // Initialize form fields if initialType is provided
    if (initialType) {
      const structure = formData(t);
      return structure.fields[initialType] || [];
    }
    return [];
  });
  const [selectedValues, setSelectedValues] = useState({});
  const [imageFile, setImageFile] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('success');
  const [isPostCreated, setIsPostCreated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const effectiveCoordinates = useSelector(selectEffectiveCoordinates);
  const effectiveLocationName = useSelector(selectEffectiveLocationName);
  const { createNewNotification, loading } = useCreateNotification(selectedType);
  const { handleUpload } = useCloudFlareImages();
  const { checkProfanity } = useProfanityFilter();

  // Set selected type from URL state (only on mount, if no initialType)
  useEffect(() => {
    if (!initialType && !selectedType && location.state?.type) {
      const typeFromState = location.state.type;
      setSelectedType(typeFromState);
      setFormFields(formStructure.fields[typeFromState] || []);
    }
  }, [initialType, location.state?.type, formStructure, selectedType]);

  // Update form fields when type changes (but not on every render)
  useEffect(() => {
    if (selectedType && formFields.length === 0) {
      setFormFields(formStructure.fields[selectedType] || []);
    }
  }, [selectedType, formFields.length, formStructure]);

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setFormFields(formStructure.fields[type] || []);
    setSelectedValues({});
    setImagePreview('');
  };

  const handleFieldChange = (name, value) => {
    const field = formFields.find(f => f.name === name);
    const otherValue = getValueByName(field, 'other');

    if (name === 'approachable') {
      const updatedValue = value === "yes" ? true : false;
      setSelectedValues((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    } else if (name === 'animalImage' || name === 'serviceImage' || name === 'psaImage') {
      setImageFile(value);
      setImagePreview(value ? URL.createObjectURL(value) : '');
    } else if (name === 'colorMarkings') {
      let updatedColors = selectedValues[name] ? [...selectedValues[name]] : [];

      if (Array.isArray(value)) {
        updatedColors = value;
      } else {
        if (!updatedColors.includes(value)) {
          updatedColors.push(value);
        } else {
          updatedColors = updatedColors.filter((color) => color !== value);
        }
      }

      setSelectedValues((prev) => ({
        ...prev,
        [name]: updatedColors
      }));
    } else if (value === otherValue) {
      setSelectedValues((prev) => ({
        ...prev,
        [name]: otherValue,
        [`${name}_custom`]: prev[`${name}_custom`] || ''
      }));
    } else {
      setSelectedValues((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting || isPostCreated) return;
    setIsSubmitting(true);
    
    const formDataObj = new FormData(e.target);
    const input = {};

    formFields.forEach((field) => {
      let value = formDataObj.get(field.name) || selectedValues[field.name];

      if (field.name === 'colorMarkings') {
        let selectedColors = selectedValues[field.name] || [];
        
        const colorField = formFields.find(f => f.name === 'colorMarkings');
        const otherValue = getValueByName(colorField, 'other');

        if (selectedValues.colorMarkings_custom) {
          selectedColors = selectedColors.filter((color) => color !== otherValue);
          selectedColors.push(selectedValues.colorMarkings_custom);
        }

        value = [...new Set(selectedColors)];
      }

      const fieldOtherValue = getValueByName(field, 'other');
      if (value === fieldOtherValue && selectedValues[`${field.name}_custom`]) {
        value = selectedValues[`${field.name}_custom`];
      }

      if (field.name === 'approachable') {
        value = selectedValues.approachable;
      }

      input[field.name] = typeof value === 'string' ? checkProfanity(value) : value;
    });

    input.language = i18n.language;

    if (Object.values(input).some((val) => typeof val === 'string' && val.includes('*'))) {
      setPopupMessage(t('profanityWarning'));
      setPopupType('error');
      setIsSubmitting(false);
      return;
    }

    try {
      let coordinates = effectiveCoordinates;
      if (coordinates) {
        input.location = { type: 'Point', coordinates };
      }

      if ((selectedType === 'psa' || selectedType === 'pet-service') && effectiveLocationName) {
        input.locationName = effectiveLocationName;
      }

      if (imageFile) {
        const uploadedImageUrl = await handleUpload([imageFile]);
        if (selectedType === 'pet-service' || selectedType === 'psa') {
          input.imageUrl = uploadedImageUrl[0];
        } else {
          input.animalImageUrl = uploadedImageUrl[0];
        }
      }

      delete input.animalImage;
      delete input.serviceImage;
      delete input.psaImage;
      await createNewNotification(input);

      setPopupMessage(t(`created_${selectedType.replace('-', '_')}`));
      setPopupType('success');
      setIsPostCreated(true);

      // If modal with callback, call it after delay
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      } else {
        // Navigate to home if not in modal
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (err) {
      setPopupMessage(t('errorCreatingPost'));
      setPopupType('error');
      setIsSubmitting(false);
    }
  };

  const typeColors = getTypeColor(selectedType);

  return (
    <motion.div
      className={`bg-white ${isModal ? '' : 'rounded-2xl shadow-lg p-6 max-w-2xl mx-auto'}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Title */}
      {!isModal && !initialType && (
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-900">
          {t('createNotification')}
        </h2>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Type Selection */}
        {!initialType && !selectedType && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {formStructure.initialQuestion}
            </label>
            <div className="grid grid-cols-1 gap-3">
              {TYPE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const colors = getTypeColor(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleTypeChange(option.value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 
                               hover:border-gray-300 hover:bg-gray-50 transition-all text-left`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <span className="font-medium text-gray-900">{t(option.label)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected Type Badge */}
        {selectedType && (
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${typeColors.bg} ${typeColors.text}`}>
            {selectedType === 'psa' && <Megaphone className="w-4 h-4" />}
            {selectedType === 'lost-animal' && <Dog className="w-4 h-4" />}
            {selectedType === 'found-animal' && <Cat className="w-4 h-4" />}
            {selectedType === 'pet-service' && <Wrench className="w-4 h-4" />}
            <span>{t(
              selectedType === 'psa' ? 'psa' : 
              selectedType === 'lost-animal' ? 'lostAnimal' : 
              selectedType === 'found-animal' ? 'foundAnimal' :
              'Pet Service'
            )}</span>
          </div>
        )}

        {/* PSA Info */}
        {selectedType === 'psa' && (
          <p className="text-sm text-gray-600 bg-amber-50 p-3 rounded-lg">
            {t('psa_main_purpose')}
          </p>
        )}

        {/* Form Fields */}
        {formFields.length > 0 && (
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {formFields.map((field, index) => (
              <FieldComponent
                key={index}
                field={field}
                onFieldChange={handleFieldChange}
                selectedValues={selectedValues}
              />
            ))}
          </motion.div>
        )}

        {/* Image Preview */}
        {imagePreview && (
          <div className="flex justify-center">
            <motion.img
              src={imagePreview}
              alt={t('selected_preview', 'Selected Preview')}
              className="max-w-full h-auto max-h-48 rounded-xl shadow-md object-cover"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            />
          </div>
        )}

        {/* Submit Button */}
        {selectedType && (
          <motion.button
            type="submit"
            disabled={isSubmitting || loading || isPostCreated}
            className={`w-full py-3 px-6 rounded-xl font-semibold text-white transition-all
                       ${isSubmitting || loading || isPostCreated 
                         ? 'bg-gray-400 cursor-not-allowed' 
                         : 'bg-primary hover:bg-primary/90 active:scale-[0.98] shadow-lg hover:shadow-xl'
                       }`}
            whileHover={!isSubmitting && !loading && !isPostCreated ? { scale: 1.01 } : undefined}
            whileTap={!isSubmitting && !loading && !isPostCreated ? { scale: 0.98 } : undefined}
          >
            {(isSubmitting || loading) ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner variant="primary" />
                <span>{t('creating', 'Creating...')}</span>
              </span>
            ) : isPostCreated ? (
              <span className="flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>{t('created', 'Created!')}</span>
              </span>
            ) : (
              t(`create_${selectedType.replace('-', '_')}`, "Create Pet Service Post")
            )}
          </motion.button>
        )}
      </form>

      {/* Popup Message */}
      <AnimatePresence>
        {popupMessage && (
          <motion.div
            className={`mt-4 p-4 rounded-xl flex items-center justify-between gap-3 ${
              popupType === 'success' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center gap-2">
              {popupType === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span className="text-sm font-medium">{popupMessage}</span>
            </div>
            <button 
              onClick={() => setPopupMessage('')}
              className="p-1 hover:bg-black/10 rounded-full transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CreatePSAForm;
